import { useState, useCallback } from "react";
import { useConfirm } from "@/hooks/useConfirm";
import axios from "axios";
import { toast } from "sonner";
import PageLoader from "@/components/PageLoader";
import PageHeader from "@/components/PageHeader";
import { useStaleData } from "@/hooks/useStaleData";
import { useAuth } from "@/context/AuthContext";
import {
  TrendingUp, PiggyBank, Share2, Pencil, Check, X, Sparkles,
  Trophy, Calendar, AlertCircle, Trash2,
} from "lucide-react";
import { fmtAmt, fmtFull } from '@/utils/format';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURR_YEAR = new Date().getFullYear();

// ── Fun comparisons (kept from original page — users love these) ─────────────
const getFunComparisons = (totalNet) => [
  { emoji: "🏢", label: "2BHK apartments in Bangalore", value: Math.floor(totalNet / 8000000), detail: "at ~₹80L each" },
  { emoji: "🚗", label: "Toyota Fortuners", value: Math.floor(totalNet / 5000000), detail: "at ~₹50L each" },
  { emoji: "✈️", label: "Round-trip Europe vacations", value: Math.floor(totalNet / 300000), detail: "at ~₹3L per trip" },
  { emoji: "📱", label: "iPhone Pro Max units", value: Math.floor(totalNet / 160000), detail: "at ~₹1.6L each" },
  { emoji: "🎓", label: "Years of IIM MBA fees", value: Math.floor(totalNet / 2500000), detail: "at ~₹25L/year" },
  { emoji: "☕", label: "Starbucks coffees", value: Math.floor(totalNet / 500), detail: "at ~₹500 each" },
];

// ── Start-year picker ────────────────────────────────────────────────────────
const StartYearPicker = ({ currentStartYear, onSaved }) => {
  const [year, setYear] = useState(currentStartYear || CURR_YEAR - 5);
  const [saving, setSaving] = useState(false);
  const { token } = useAuth();
  const years = [];
  for (let y = CURR_YEAR; y >= 1970; y--) years.push(y);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`,
        { career_start_year: year },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Start year saved");
      onSaved(year);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save start year");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 shadow-sm p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl bg-orange-50 dark:bg-orange-950/40 flex items-center justify-center shrink-0">
          <Calendar size={18} className="text-orange-600" />
        </div>
        <div>
          <p className="font-bold text-stone-800 dark:text-stone-100">When did you start earning?</p>
          <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
            Pick the year you started your first job. We'll build your career earnings from there to today.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="border border-stone-200 dark:border-stone-700 rounded-xl px-4 py-2.5 text-sm font-semibold bg-white dark:bg-stone-800 text-stone-800 dark:text-stone-100 focus:outline-none focus:border-orange-400"
        >
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white text-sm font-bold rounded-xl transition-colors"
        >
          {saving ? "Saving…" : "Save start year"}
        </button>
      </div>
    </div>
  );
};

// ── CTC edit modal ────────────────────────────────────────────────────────────
const CtcEditModal = ({ row, onSave, onClose }) => {
  const [ctc, setCtc] = useState("");
  const [saving, setSaving] = useState(false);

  // Always hydrate from row when modal opens
  useEffect(() => {
    if (!row) return;
    setCtc(row.ctc > 0 ? String(Math.round(row.ctc)) : "");
  }, [row]);

  if (!row) return null;

  const save = async () => {
    const n = Number(ctc);
    if (!n || n < 0) { toast.error("Enter a valid CTC"); return; }
    setSaving(true);
    try { await onSave(row.year, n); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="bg-white dark:bg-stone-900 rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <p className="font-bold text-stone-800 dark:text-stone-100 text-base">{row.year} — Annual CTC</p>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 dark:hover:bg-stone-800"><X size={16} /></button>
        </div>
        <div className="relative mb-4">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm font-semibold">₹</span>
          <input
            type="number"
            value={ctc}
            onChange={e => setCtc(e.target.value)}
            onKeyDown={e => e.key === "Enter" && save()}
            placeholder="e.g. 1200000"
            autoFocus
            className="w-full border border-stone-200 dark:border-stone-700 rounded-xl pl-7 pr-3 py-3 text-sm font-semibold bg-white dark:bg-stone-800 text-stone-800 dark:text-stone-100 focus:outline-none focus:border-orange-400"
          />
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 text-sm font-semibold text-stone-600 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white text-sm font-bold transition-colors">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Row in the year-by-year table ────────────────────────────────────────────
const YearRow = ({ row, onEdit, onDelete }) => {
  const taxPct = row.ctc > 0 ? ((row.tax / row.ctc) * 100).toFixed(1) : 0;
  return (
    <tr
      className={`border-b border-stone-100 dark:border-stone-800 cursor-pointer hover:bg-stone-50 dark:hover:bg-stone-800/40 transition-colors ${!row.has_entry ? "bg-amber-50/40 dark:bg-amber-950/20" : ""}`}
      onClick={() => onEdit(row)}
    >
      <td className="py-3 px-3 font-bold text-stone-800 dark:text-stone-100 text-sm">{row.year}</td>
      <td className="py-3 px-3">
        {row.has_entry ? (
          <span className="text-sm font-semibold text-stone-800 dark:text-stone-100 flex items-center gap-1.5">
            {fmtFull(row.ctc)} <Pencil size={10} className="text-stone-400" />
          </span>
        ) : (
          <span className="text-xs text-amber-600 dark:text-amber-400 font-semibold">Tap to add</span>
        )}
      </td>
      <td className="py-3 px-3 text-sm font-semibold text-red-600">
        {row.ctc > 0 ? fmtAmt(row.tax) : "—"}
        {row.ctc > 0 && <span className="text-[10px] text-stone-400 ml-1">({taxPct}%)</span>}
      </td>
      <td className="py-3 px-3 text-sm font-bold text-emerald-600">
        {row.ctc > 0 ? fmtAmt(row.net) : "—"}
      </td>
      <td className="py-3 px-3 text-right" onClick={e => e.stopPropagation()}>
        {row.has_entry && (
          <button onClick={() => onDelete(row.year)} className="p-1.5 rounded-lg text-stone-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors">
            <Trash2 size={13} />
          </button>
        )}
      </td>
    </tr>
  );
};

// ── Main ─────────────────────────────────────────────────────────────────────
export default function LifetimeEarnings() {
  const { token, user, refreshUser } = useAuth();
  const confirm = useConfirm();
  const [shareMsg, setShareMsg] = useState("");
  const [editTarget, setEditTarget] = useState(null);

  const fetchSummary = useCallback(async () => {
    const res = await axios.get(`${API}/yearly-earnings/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  }, [token]);

  const { data: summary, loading, reload } = useStaleData(
    "bm_yearly_earnings_cache",
    fetchSummary,
    { errorMsg: "Failed to load career earnings", fallback: null }
  );

  const saveCtc = useCallback(async (year, ctc) => {
    try {
      await axios.post(`${API}/yearly-earnings`,
        { year, ctc_annual: ctc },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${year} saved`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
      throw e;
    }
  }, [token, reload]);

  const deleteYear = useCallback(async (year) => {
    if (!(await confirm({ title: `Delete ${year} entry?`, body: 'This removes the CTC record for this year permanently.', confirm: 'Delete', danger: true }))) return;
    try {
      await axios.delete(`${API}/yearly-earnings/${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${year} removed`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  }, [token, reload]);

  const onStartYearSaved = useCallback((_y) => {
    // Refresh user in AuthContext so career_start_year propagates
    if (refreshUser) refreshUser();
    reload();
  }, [refreshUser, reload]);

  const handleShare = useCallback(() => {
    if (!summary) return;
    const text = `My Career Earnings (${summary.start_year}–${summary.end_year}):\n` +
      `Total Gross: ${fmtFull(summary.total_gross)}\n` +
      `Total Tax: ${fmtFull(summary.total_tax)}\n` +
      `Net Take-home: ${fmtFull(summary.total_net)}\n` +
      `Across ${summary.years_tracked} years tracked\n\n` +
      `Tracked via BudgetMantra.in`;
    if (navigator.share) {
      navigator.share({ title: "My Career Earnings", text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).then(() => {
        setShareMsg("Copied to clipboard!");
        setTimeout(() => setShareMsg(""), 2500);
      }).catch(() => {});
    }
  }, [summary]);

  if (loading) {
    return (
      <>
        <div className="min-h-[calc(100vh-80px)] bg-[#fffaf5] dark:bg-stone-950 flex items-center justify-center">
          <PageLoader
            message="Crunching your career…"
            tips={[
              "Pulling your year-by-year CTC…",
              "Computing tax under the New Regime…",
              "Adding up nine figures (hopefully)…",
            ]}
          />
        </div>
      </>
    );
  }

  // If user already set a start year (stored on their profile), don't re-show
  // the setup card — jump straight to the earnings recording view.
  const needsStartYear = !summary?.start_year && !user?.career_start_year;
  const effectiveTaxRate = summary && summary.total_gross > 0
    ? ((summary.total_tax / summary.total_gross) * 100).toFixed(1)
    : 0;
  const gapsCount = summary?.yearly?.filter(y => !y.has_entry).length || 0;
  const comparisons = summary ? getFunComparisons(summary.total_net) : [];

  return (
    <>
      <PageHeader
        icon={<TrendingUp size={20} />}
        title="Lifetime Earnings"
        subtitle="Your complete earning history"
        color="orange"
      >
        {summary?.years_tracked > 0 && (
          <button onClick={handleShare}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300 hover:border-orange-300 transition-colors">
            <Share2 size={13} /> Share
          </button>
        )}
      </PageHeader>
      <div className="min-h-screen bg-[#fffaf5] dark:bg-stone-950">
        <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6 pb-24 lg:pb-6">

          {shareMsg && (
            <div className="mb-4 text-xs text-emerald-600 font-semibold">{shareMsg}</div>
          )}

          {needsStartYear ? (
            <StartYearPicker currentStartYear={user?.career_start_year} onSaved={onStartYearSaved} />
          ) : !summary ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center space-y-2">
                <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-sm text-stone-400">Loading your career earnings…</p>
              </div>
            </div>
          ) : (
            <>
              {/* Hero numbers */}
              <div className="bg-gradient-to-br from-orange-500 via-orange-500 to-amber-500 rounded-3xl p-6 mb-6 text-white shadow-xl shadow-orange-500/20">
                <div className="flex items-center gap-2 mb-1 text-white/80 text-xs font-semibold uppercase tracking-wider">
                  <Sparkles size={12} /> {summary.start_year ?? user?.career_start_year} – {summary.end_year}
                </div>
                <p className="text-4xl lg:text-5xl font-bold font-['Outfit'] mb-1">{fmtAmt(summary.total_net)}</p>
                <p className="text-white/80 text-sm font-medium">Net take-home across {summary.years_tracked} {summary.years_tracked === 1 ? "year" : "years"}</p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
                  <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-white/70 mb-1">Gross</p>
                    <p className="text-base lg:text-lg font-bold font-['Outfit']">{fmtAmt(summary.total_gross)}</p>
                  </div>
                  <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-white/70 mb-1">Tax</p>
                    <p className="text-base lg:text-lg font-bold font-['Outfit']">{fmtAmt(summary.total_tax)}</p>
                    <p className="text-[9px] text-white/60">{effectiveTaxRate}% effective</p>
                  </div>
                  <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-white/70 mb-1">Avg CTC</p>
                    <p className="text-base lg:text-lg font-bold font-['Outfit']">{fmtAmt(summary.avg_annual_ctc)}</p>
                  </div>
                </div>
              </div>

              {/* Gaps banner */}
              {gapsCount > 0 && (
                <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-2xl p-4 mb-5">
                  <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-amber-800 dark:text-amber-200">{gapsCount} {gapsCount === 1 ? "year" : "years"} missing</p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                      Fill in your CTC for every year to get the complete picture. Rows highlighted below need your input.
                    </p>
                  </div>
                </div>
              )}

              {/* Year-by-year table */}
              <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 shadow-sm overflow-hidden mb-6">
                <div className="px-5 py-4 border-b border-stone-100 dark:border-stone-800 flex items-center justify-between">
                  <p className="text-sm font-bold text-stone-700 dark:text-stone-300">Year-by-year</p>
                  <p className="text-[11px] text-stone-400 dark:text-stone-500">New Regime · FY 2025-26 slabs</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 dark:bg-stone-800/50 text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400">
                      <tr>
                        <th className="text-left py-2.5 px-3 font-semibold">Year</th>
                        <th className="text-left py-2.5 px-3 font-semibold">Annual CTC</th>
                        <th className="text-left py-2.5 px-3 font-semibold">Tax</th>
                        <th className="text-left py-2.5 px-3 font-semibold">Take-home</th>
                        <th className="py-2.5 px-3 w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(summary.yearly || []).map(row => (
                        <YearRow key={row.year} row={row} onEdit={setEditTarget} onDelete={deleteYear} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Fun comparisons */}
              {summary.total_net > 0 && (
                <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 shadow-sm p-5 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <PiggyBank size={16} className="text-orange-500" />
                    <p className="text-sm font-bold text-stone-700 dark:text-stone-300">Fun reality check</p>
                  </div>
                  <p className="text-xs text-stone-500 dark:text-stone-400 mb-4">
                    Your {fmtAmt(summary.total_net)} in take-home could have bought…
                  </p>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {comparisons.filter(c => c.value > 0).map(c => (
                      <div key={c.label} className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-3">
                        <div className="text-2xl mb-1">{c.emoji}</div>
                        <p className="text-lg font-bold font-['Outfit'] text-stone-800 dark:text-stone-100">
                          {c.value.toLocaleString("en-IN")}
                        </p>
                        <p className="text-xs text-stone-500 dark:text-stone-400 leading-tight">{c.label}</p>
                        <p className="text-[10px] text-stone-400 mt-1">{c.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Change start year */}
              <details className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 shadow-sm">
                <summary className="cursor-pointer px-5 py-3 text-sm font-semibold text-stone-600 dark:text-stone-300 list-none flex items-center justify-between">
                  <span className="flex items-center gap-2"><Calendar size={14} /> Change start year</span>
                  <TrendingUp size={14} className="text-stone-400" />
                </summary>
                <div className="px-5 pb-5">
                  <StartYearPicker currentStartYear={summary.start_year} onSaved={onStartYearSaved} />
                </div>
              </details>
            </>
          )}
        </div>
      </div>

      <CtcEditModal
        row={editTarget}
        onSave={saveCtc}
        onClose={() => setEditTarget(null)}
      />
    </>
  );
}
